/** Helpers for load.driverPayout map: { [truckId]: { payout, truckNumber } }. */

export function normalizeDriverPayoutMap(driverPayout) {
    if (!driverPayout || typeof driverPayout !== 'object' || Array.isArray(driverPayout)) {
        return {};
    }
    return driverPayout;
}

export function driverPayoutEntries(driverPayout) {
    const map = normalizeDriverPayoutMap(driverPayout);
    return Object.keys(map).map((truckId) => {
        const entry = map[truckId] || {};
        return {
            truckId: String(truckId),
            payout: entry.payout === undefined ? null : entry.payout,
            truckNumber: entry.truckNumber || String(truckId),
        };
    });
}

/** True when at least one truck has a non-null payout. */
export function hasAnyExplicitDriverPayout(driverPayout) {
    return driverPayoutEntries(driverPayout).some((e) => e.payout !== null && e.payout !== undefined && e.payout !== '');
}

export function isLeaseOrOwnerTruckType(truckType) {
    if (truckType == null || truckType === '') return false;
    // DB/JPA ordinals: Company=0, Lease=1, Owner=2
    if (truckType === 1 || truckType === 2) return true;
    const raw = typeof truckType === 'object' && truckType != null && truckType.name != null
        ? truckType.name
        : truckType;
    const s = String(raw).trim().toLowerCase();
    return s === 'lease' || s === 'owner';
}

export function isCompanyTruckType(truckType) {
    if (truckType == null || truckType === '') return false;
    if (truckType === 0) return true;
    const raw = typeof truckType === 'object' && truckType != null && truckType.name != null
        ? truckType.name
        : truckType;
    return String(raw).trim().toLowerCase() === 'company';
}

function executorByTruckId(data) {
    const byId = new Map();
    const executors = Array.isArray(data?.loadExecutors) ? data.loadExecutors : [];
    for (const executor of executors) {
        if (executor?.id == null) continue;
        byId.set(String(executor.id), executor);
    }
    return byId;
}

function truckTypeFor(data, truckId) {
    const id = String(truckId);
    const current = data?.currentTruck;
    if (current?.id != null && String(current.id) === id) return current.truckType;
    return executorByTruckId(data).get(id)?.truckType;
}

/** True when a driver currently assigned to this truck is paid by percentage. */
export function companyTruckHasPercentageDriver(data, truckId) {
    const pay = data?.driverPaymentTypesByTruckId?.[String(truckId)];
    if (!pay) return false;
    return pay.driver1 === 'Percentage' || pay.driver2 === 'Percentage';
}

export function eligibleForDriverPayout(data, truckId, truckType) {
    if (truckId == null) return false;
    if (isCompanyTruckType(truckType)) return companyTruckHasPercentageDriver(data, truckId);
    if (isLeaseOrOwnerTruckType(truckType)) return true;
    return truckType == null || truckType === '';
}

/** True when Payment should show Driver Payout for this load. */
export function shouldShowDriverPayout(data) {
    if (!data) return false;
    return resolveDriverPayoutTrucks(data).length > 0;
}

/**
 * Resolve trucks for Payment view/edit. Prefer driverPayout map; otherwise current
 * non-company truck so the Driver Payout input is always available for lease/owner.
 * Order: oldest first (loadExecutors order), then any remaining map entries.
 */
export function resolveDriverPayoutTrucks(data) {
    const loadHasStarted = Boolean(data?.loadHasStarted);
    const map = normalizeDriverPayoutMap(data?.driverPayout);
    const current = data?.currentTruck;
    const mapEntries = driverPayoutEntries(map);
    const currentId = current?.id != null ? String(current.id) : null;
    const currentEligible = currentId != null && eligibleForDriverPayout(data, currentId, current?.truckType);

    const payoutFromMap = (truckId) => {
        const entry = map[truckId];
        if (!entry) return null;
        return entry.payout === undefined ? null : entry.payout;
    };

    let trucks = [];

    if (!loadHasStarted) {
        if (currentEligible) {
            const match = mapEntries.find((e) => e.truckId === currentId);
            trucks = [{
                truckId: currentId,
                truckNumber: current.truckNumber || match?.truckNumber || currentId,
                payout: match ? match.payout : payoutFromMap(currentId),
            }];
        }
    } else if (mapEntries.length > 0 || currentEligible) {
        const byId = new Map();
        for (const entry of mapEntries) {
            const type = truckTypeFor(data, entry.truckId);
            if (!eligibleForDriverPayout(data, entry.truckId, type)) continue;
            byId.set(entry.truckId, { ...entry });
        }
        if (currentEligible) {
            if (!byId.has(currentId)) {
                byId.set(currentId, {
                    truckId: currentId,
                    truckNumber: current.truckNumber || currentId,
                    payout: null,
                });
            } else {
                const existing = byId.get(currentId);
                existing.truckNumber = current.truckNumber || existing.truckNumber;
            }
        }
        const executors = Array.isArray(data?.loadExecutors) ? data.loadExecutors : [];
        for (const ex of executors) {
            if (ex?.id == null) continue;
            const id = String(ex.id);
            if (byId.has(id) && ex.truckNumber) {
                byId.get(id).truckNumber = ex.truckNumber;
            }
        }
        trucks = Array.from(byId.values());
    }

    return orderDriverPayoutTrucksOldestFirst(trucks, data?.loadExecutors);
}

/** Oldest executor first; any trucks not in executors keep relative order at the end. */
export function orderDriverPayoutTrucksOldestFirst(trucks, loadExecutors) {
    if (!trucks || trucks.length === 0) return [];
    if (trucks.length === 1) return trucks;

    const byId = new Map(trucks.map((t) => [String(t.truckId), t]));
    const ordered = [];
    const seen = new Set();

    if (Array.isArray(loadExecutors)) {
        for (const ex of loadExecutors) {
            if (ex?.id == null) continue;
            const id = String(ex.id);
            if (byId.has(id) && !seen.has(id)) {
                ordered.push(byId.get(id));
                seen.add(id);
            }
        }
    }

    for (const t of trucks) {
        const id = String(t.truckId);
        if (!seen.has(id)) {
            ordered.push(t);
            seen.add(id);
        }
    }
    return ordered;
}

export function formatDriverPayoutDisplay(driverPayout, { hideAmount = false, canSeeRates = true, loadHasStarted = true, trucks = null } = {}) {
    let entries = Array.isArray(trucks) && trucks.length > 0
        ? trucks.map((t) => ({
            truckId: String(t.truckId),
            payout: t.payout === undefined ? null : t.payout,
            truckNumber: t.truckNumber || String(t.truckId),
        }))
        : driverPayoutEntries(driverPayout);
    // Mirror Rate Distribution by Trucks: no multi-truck payout UI until load has started.
    if (!loadHasStarted && entries.length > 1) {
        entries = entries.slice(0, 1);
    }
    const withAmount = entries.filter((e) => e.payout !== null && e.payout !== undefined && e.payout !== '');
    const anySet = withAmount.length > 0;
    const amounts = withAmount.map((e) => Number(e.payout));
    const isUnset = (e) => e.payout === null || e.payout === undefined || e.payout === '';

    // Updaters: never expose $. Show Set / Not Set only (single: under "Driver Payout";
    // multi: per truck number).
    if (hideAmount) {
        if (entries.length === 0) {
            return {
                text: 'Not Set',
                isSet: false,
                className: 'payoutNotSet',
                columns: [{ label: 'Driver Payout', text: 'Not Set', className: 'payoutNotSet' }],
            };
        }
        if (entries.length === 1) {
            const set = !isUnset(entries[0]);
            return {
                text: set ? 'Set' : 'Not Set',
                isSet: set,
                className: set ? 'payoutSet' : 'payoutNotSet',
                columns: [{
                    label: 'Driver Payout',
                    text: set ? 'Set' : 'Not Set',
                    className: set ? 'payoutSet' : 'payoutNotSet',
                    truckNumber: entries[0].truckNumber,
                    amount: null,
                }],
            };
        }
        const columns = entries.map((e) => {
            const set = !isUnset(e);
            return {
                label: `#${e.truckNumber}`,
                text: set ? 'Set' : 'Not Set',
                className: set ? 'payoutSet' : 'payoutNotSet',
                truckNumber: e.truckNumber,
                amount: null,
            };
        });
        return {
            text: columns.map((c) => `${c.label}: ${c.text}`).join('  '),
            isSet: anySet,
            className: '',
            columns,
        };
    }
    if (!canSeeRates) {
        return { text: '-', isSet: false, className: '', columns: [] };
    }
    if (entries.length === 0) {
        return { text: '-', isSet: false, className: '', columns: [] };
    }
    if (entries.length === 1) {
        const e = entries[0];
        if (isUnset(e)) {
            return {
                text: 'Not Set',
                isSet: false,
                className: 'payoutNotSet',
                amounts: [],
                columns: [{
                    label: 'Driver Payout',
                    text: 'Not Set',
                    className: 'payoutNotSet',
                    truckNumber: e.truckNumber,
                    amount: null,
                }],
            };
        }
        return {
            text: `$${e.payout}`,
            isSet: true,
            className: '',
            amount: Number(e.payout),
            amounts: [Number(e.payout)],
            columns: [{
                label: 'Driver Payout',
                text: `$${e.payout}`,
                truckNumber: e.truckNumber,
                amount: Number(e.payout),
            }],
        };
    }
    // Multi-truck: one Payment-section column per truck (truck number as label)
    const columns = entries.map((e) => {
        const unset = isUnset(e);
        return {
            label: `#${e.truckNumber}`,
            text: unset ? 'Not Set' : `$${e.payout}`,
            className: unset ? 'payoutNotSet' : '',
            truckNumber: e.truckNumber,
            amount: unset ? null : Number(e.payout),
        };
    });
    const parts = columns.map((c) => `${c.label}: ${c.text}`);
    return {
        text: parts.join('  '),
        isSet: anySet,
        className: '',
        amounts,
        columns,
    };
}

export function sumExplicitDriverPayouts(driverPayout) {
    return driverPayoutEntries(driverPayout).reduce((sum, e) => {
        if (e.payout === null || e.payout === undefined || e.payout === '') return sum;
        return sum + (Number(e.payout) || 0);
    }, 0);
}

/** Rate when manually adding a load onto a percentage statement. */
export function resolveStatementAddLoadRate(loadData) {
    const entries = driverPayoutEntries(loadData?.driverPayout);
    const explicit = entries.filter((e) => e.payout !== null && e.payout !== undefined && e.payout !== '');
    if (explicit.length === 1) {
        return { rate: Number(explicit[0].payout), workingWithDriverPayout: true };
    }
    if (explicit.length > 1) {
        // Ambiguous multi-truck; use first explicit amount as a starting point for the accountant.
        return { rate: Number(explicit[0].payout), workingWithDriverPayout: true };
    }
    return { rate: loadData?.amountForInvoice, workingWithDriverPayout: false };
}

export function calculateTruckDriverPayoutTotal(thisWeeksDriverPayoutLoads) {
    if (!thisWeeksDriverPayoutLoads || !Array.isArray(thisWeeksDriverPayoutLoads)) return 0;
    let total = 0;
    for (const load of thisWeeksDriverPayoutLoads) {
        if (!load) continue;
        const loadData = typeof load === 'string' ? JSON.parse(load) : load;
        if (loadData && loadData.driverPayoutAmount != null && loadData.driverPayoutAmount !== '') {
            total += Number(loadData.driverPayoutAmount) || 0;
        }
    }
    return total;
}

export function sanitizeThisWeeksDriverPayoutLoadsAmounts(loads) {
    if (!loads || !Array.isArray(loads)) return loads;
    return loads.map((load) => {
        if (load == null) return load;
        const loadData = typeof load === 'string' ? JSON.parse(load) : load;
        if (!loadData || typeof loadData !== 'object') return load;
        return { ...loadData, driverPayoutAmount: null };
    });
}
